import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import api from "@/lib/api";
import { fmtDMY, todayNZ } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileDown, Printer, Lock, CalendarDays, Loader2, ChevronsUpDown, ChevronsDownUp, Plus } from "lucide-react";
import { RosterPending } from "@/components/RosterPending";
import { RosterSentLog } from "@/components/RosterSentLog";
import { RosterTools } from "@/components/RosterTools";
import { RosterTable, useRoster, ROSTER_LEAVE } from "@/components/RosterTable";

export { ROSTER_LEAVE };

function shift(date, days) {
  const d = new Date(date + "T12:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function RosterGrid({ editable, endpoint = "/roster", compact = false, initialDate }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(initialDate || todayNZ());
  const [expanded, setExpanded] = useState(false);
  const [extra, setExtra] = useState(3);   // periods stacked below the current one when expanded
  const [exporting, setExporting] = useState(false);
  const ref = useRef(null);
  const stackRefs = useRef({});
  const { data } = useRoster(endpoint, date);
  const periodDays = data ? data.days.length : 7;
  const canEdit = editable && data && !data.locked;
  const stack = expanded && data ? Array.from({ length: extra }, (_, i) => shift(data.start, periodDays * (i + 1))) : [];

  const exportPdf = async (print) => {
    if (!ref.current) return;
    setExporting(true);
    try {
      const nodes = [ref.current, ...stack.map((d) => stackRefs.current[d]).filter(Boolean)];
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      let y = 10, lastEnd = data.end;
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const png = await toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: node.scrollWidth, height: node.scrollHeight });
        const ratio = Math.min((pw - 20) / node.scrollWidth, (ph - 30) / node.scrollHeight);
        const imgH = node.scrollHeight * ratio;
        const cap = node.closest("[data-testid^='roster-period-']")?.querySelector("[data-testid^='roster-period-caption-']")?.textContent?.trim();
        const title = i === 0 ? `${data.motel_name} — ${t("Roster")} ${fmtDMY(data.start)} – ${fmtDMY(data.end)}` : (cap || "").replace(/\s*(CURRENT|LOCKED)\s*$/i, "");
        if (y + 8 + imgH > ph - 8) { pdf.addPage(); y = 10; }
        pdf.setFontSize(i === 0 ? 13 : 10); pdf.setFont(undefined, "bold");
        pdf.text(title, 10, y + 2); y += 6;
        pdf.addImage(png, "PNG", 10, y, node.scrollWidth * ratio, imgH); y += imgH + 8;
        const m = (cap || "").match(/(\d{2})\/(\d{2})\/(\d{4})\s*$/);
        if (m) lastEnd = `${m[3]}-${m[2]}-${m[1]}`;
      }
      if (editable) {
        const log = (await api.get(`/roster/sent?start=${data.start}&end=${lastEnd}`)).data.items || [];
        if (log.length) {
          y += 2;
          const line = (txt, size = 8, bold = false) => {
            if (y > ph - 10) { pdf.addPage(); y = 14; }
            pdf.setFontSize(size); pdf.setFont(undefined, bold ? "bold" : "normal");
            pdf.text(txt, 10, y); y += size * 0.5 + 1.5;
          };
          line(t("Sent roster changes"), 10, true);
          log.forEach((it) => {
            line(`${it.cleaner_name} — ${t("sent by {{name}}", { name: it.sent_by })} ${new Date(it.sent_at).toLocaleString()} · ${it.emailed ? t("Email sent") : t("In-app message only")} · ${it.seen ? t("Seen by cleaner") : t("Not yet seen")}`, 8, true);
            it.lines.forEach((ln) => line(`   ${ln.replace(/→/g, "->")}`));
          });
        }
      }
      if (print) {
        pdf.autoPrint();
        window.open(pdf.output("bloburl"), "_blank");
      } else {
        pdf.save(`roster-${data.start}.pdf`);
      }
    } catch (e) {
      toast.error(t("Could not build the PDF"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="roster-grid">
      {editable && <RosterPending />}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setDate(shift(date, -periodDays))} data-testid="roster-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm font-semibold" data-testid="roster-period">
          {data ? `${fmtDMY(data.start)} – ${fmtDMY(data.end)}` : "…"}
          {data?.locked && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700" data-testid="roster-locked"><Lock className="h-3 w-3" /> {t("Locked")}</span>}
        </span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setDate(shift(date, periodDays))} data-testid="roster-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => setDate(todayNZ())} data-testid="roster-today"><CalendarDays className="h-3.5 w-3.5" /> {t("Current")}</Button>
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-8 w-40" data-testid="roster-date" />
        {!compact && (
          <span className="ml-auto flex flex-wrap gap-1">
            <Button variant={expanded ? "secondary" : "outline"} size="sm" className="h-8 gap-1 text-xs" onClick={() => { setExpanded((v) => !v); setExtra(3); }} data-testid="roster-expand">
              {expanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />} {expanded ? t("Collapse") : t("Expand")}
            </Button>
            {editable && <RosterTools data={data} shiftDate={shift} />}
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={!data || exporting} onClick={() => exportPdf(false)} data-testid="roster-pdf">
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={!data || exporting} onClick={() => exportPdf(true)} data-testid="roster-print"><Printer className="h-3.5 w-3.5" /> {t("Print")}</Button>
          </span>
        )}
      </div>
      <div className={expanded ? "overflow-y-auto pr-1" : ""} style={expanded ? { scrollbarGutter: "stable" } : undefined}>
        <RosterTable ref={ref} date={date} editable={editable} endpoint={endpoint} caption={expanded} />
      </div>
      {canEdit && <p className="text-[11px] text-muted-foreground">{t("Tap a day to set ON, OFF or a leave type. Changes apply immediately, but cleaners are only told when you Save / send.")}</p>}
      {stack.length > 0 && (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }} data-testid="roster-stack">
          {stack.map((d) => <RosterTable key={d} ref={(el) => { stackRefs.current[d] = el; }} date={d} editable={editable} endpoint={endpoint} caption />)}
          <Button variant="ghost" size="sm" className="h-8 w-full gap-1 text-xs" onClick={() => setExtra((n) => n + 3)} data-testid="roster-more">
            <Plus className="h-3.5 w-3.5" /> {t("Show 3 more periods")}
          </Button>
        </div>
      )}
      {editable && data && <RosterSentLog start={data.start} end={data.end} />}
    </div>
  );
}
